import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useCart } from "../Context/CartContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getImageUrl, MEDIA_BASE_URL } from "../services/indexMediaBaseUrl";

export default function AddToCartDialog({ product = null, open = false, onClose = () => {}, onAdded = () => {} }) {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("AddToCartDialog");
  let lang = (i18n && i18n.language) || localStorage.getItem("lang") || "ar";
  if (lang === "rn") lang = "en";
  lang = lang.startsWith("ar") ? "ar" : "en";
  const isAr = lang === "ar";
  const getName = (p) => (isAr ? (p?.name_ar || p?.name_en) : (p?.name_en || p?.name_ar));
  const getDesc = (p) => (isAr ? (p?.desc_ar || p?.desc_en) : (p?.desc_en || p?.desc_ar));

  const [qty, setQty] = useState(1);
  const [sizeId, setSizeId] = useState("");
  const [colorId, setColorId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [added, setAdded] = useState(false);

  useEffect(() => {
    if (!open) {
      setQty(1);
      setSizeId("");
      setColorId("");
      setLoading(false);
      setError("");
      setAdded(false);
    } else if (product) {
      setSizeId((product.sizes && product.sizes[0] && product.sizes[0].id) || "");
      setColorId((product.colors && product.colors[0] && product.colors[0].id) || "");
    }
  }, [open, product]);

  const resolveImageSrc = (src) => {
    if (!src) return null;
    try { return getImageUrl(src); }
    catch { return MEDIA_BASE_URL ? `${MEDIA_BASE_URL.replace(/\/$/,'')}/${String(src).replace(/^\/+/,'')}` : src; }
  };

  const getPriceInfo = () => {
    if (!product) return null;
    const currencies = product.currencies || product.Currencies || [];
    let entry = currencies[0] || null;
    if (!entry) return { price: product.price, discount: product.price_after_discount };
    const p = parseFloat(entry.price) || 0;
    const d = parseFloat(entry.price_after_discount) || 0;
    return { price: p, discount: d > 0 && d < p ? d : null };
  };

  const handleAdd = async () => {
    setError("");
    const userRaw = localStorage.getItem("user");
    if (!userRaw) { navigate("/login"); return; }
    if (!product || !product.id) return;
    const payload = { product_id: product.id, quantity: Number(qty) || 1 };
    if (sizeId) payload.size_id = sizeId;
    if (colorId) payload.color_id = colorId;
    try {
      setLoading(true);
      await addToCart(payload);
      setAdded(true);
      setTimeout(() => { onAdded(product); onClose(); }, 900);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t("failed_add_cart"));
    } finally {
      setLoading(false);
    }
  };

  if (!product) return null;

  const priceInfo = getPriceInfo();
  const imageSrc = resolveImageSrc(
    product.main_photo || (product.images && product.images[0] && product.images[0].image) || ""
  ) || "/placeholder.png";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(0,0,0,0.15)",
        },
      }}
    >
      {/* Header image banner */}
      <Box sx={{ position: "relative", height: 220, bgcolor: "grey.50", overflow: "hidden" }}>
        <Box
          component="img"
          src={imageSrc}
          alt={getName(product)}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "transform 0.4s ease",
          }}
          onError={(e) => { e.currentTarget.src = "/placeholder.png"; }}
        />
        {/* gradient overlay */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(180deg, transparent 40%, rgba(0,0,0,0.55) 100%)",
          }}
        />
        {/* close button */}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            bgcolor: "rgba(255,255,255,0.85)",
            backdropFilter: "blur(4px)",
            "&:hover": { bgcolor: "#fff" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        {/* price badge */}
        {priceInfo && (
          <Box sx={{ position: "absolute", bottom: 14, left: 16 }}>
            {priceInfo.discount ? (
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "rgba(255,255,255,0.7)", textDecoration: "line-through", display: "block", lineHeight: 1 }}
                >
                  {priceInfo.price} {isAr ? "ج.م" : "EGP"}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h6" fontWeight={800} color="#fff">
                    {priceInfo.discount} {isAr ? "ج.م" : "EGP"}
                  </Typography>
                  <Chip
                    label={`-${Math.round((1 - priceInfo.discount / priceInfo.price) * 100)}%`}
                    size="small"
                    sx={{ bgcolor: "error.main", color: "#fff", fontWeight: 800, height: 22, fontSize: "0.7rem" }}
                  />
                </Box>
              </Box>
            ) : priceInfo.price ? (
              <Typography variant="h6" fontWeight={800} color="#fff">
                {priceInfo.price} {isAr ? "ج.م" : "EGP"}
              </Typography>
            ) : null}
          </Box>
        )}
      </Box>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
        {/* Product name */}
        <Typography variant="h6" fontWeight={800} mb={0.5} lineHeight={1.3}>
          {getName(product)}
        </Typography>
        {getDesc(product) && (
          <Typography
            variant="body2"
            color="text.secondary"
            mb={2}
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {getDesc(product)}
          </Typography>
        )}

        {/* Sizes */}
        {product.sizes && product.sizes.length > 0 && (
          <Box mb={2.5}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 1, display: "block", mb: 1 }}>
              {isAr ? "المقاس" : "Size"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {product.sizes.map((s) => (
                <Chip
                  key={s.id}
                  label={s.size || s.key}
                  onClick={() => setSizeId(s.id)}
                  variant={sizeId === s.id ? "filled" : "outlined"}
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    cursor: "pointer",
                    borderColor: sizeId === s.id ? "primary.main" : "grey.300",
                    bgcolor: sizeId === s.id ? "primary.main" : "transparent",
                    color: sizeId === s.id ? "#fff" : "text.primary",
                    transition: "all 0.2s ease",
                    "&:hover": { borderColor: "primary.main", bgcolor: sizeId === s.id ? "primary.dark" : "rgba(213,170,47,0.08)" },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Colors */}
        {product.colors && product.colors.length > 0 && (
          <Box mb={2.5}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 1, display: "block", mb: 1 }}>
              {isAr ? "اللون" : "Color"}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
              {product.colors.map((c) => (
                <Tooltip key={c.id} title={isAr ? (c.color_ar || c.color_en) : (c.color_en || c.color_ar)} arrow>
                  <Box
                    onClick={() => setColorId(c.id)}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      bgcolor: c.code || "grey.300",
                      border: colorId === c.id ? "3px solid" : "2px solid",
                      borderColor: colorId === c.id ? "primary.main" : "grey.200",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      boxShadow: colorId === c.id ? "0 0 0 2px rgba(213,170,47,0.4)" : "0 1px 4px rgba(0,0,0,0.15)",
                      transform: colorId === c.id ? "scale(1.15)" : "scale(1)",
                      "&:hover": { transform: "scale(1.15)", boxShadow: "0 0 0 2px rgba(213,170,47,0.35)" },
                    }}
                  />
                </Tooltip>
              ))}
            </Box>
          </Box>
        )}

        {/* Quantity */}
        <Box mb={2}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 1, display: "block", mb: 1 }}>
            {isAr ? "الكمية" : "Quantity"}
          </Typography>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              border: "1.5px solid",
              borderColor: "grey.200",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <IconButton
              size="small"
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              sx={{ borderRadius: 0, width: 38, height: 38, "&:hover": { bgcolor: "grey.100" } }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <Typography
              fontWeight={800}
              sx={{ minWidth: 40, textAlign: "center", fontSize: "1rem", userSelect: "none" }}
            >
              {qty}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setQty((q) => q + 1)}
              sx={{ borderRadius: 0, width: 38, height: 38, "&:hover": { bgcolor: "grey.100" } }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ borderRadius: 2, mb: 1 }}>{error}</Alert>}
        {added && (
          <Alert severity="success" sx={{ borderRadius: 2, mb: 1 }}>
            {isAr ? "تمت الإضافة إلى السلة ✓" : "Added to cart ✓"}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 3,
            fontWeight: 700,
            textTransform: "none",
            flex: 0.4,
            py: 1.4,
            borderWidth: 1.5,
            "&:hover": { borderWidth: 1.5 },
          }}
        >
          {t("cancel")}
        </Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          disabled={loading || added}
          startIcon={loading ? null : <ShoppingCartIcon />}
          sx={{
            flex: 1,
            borderRadius: 3,
            py: 1.4,
            fontWeight: 800,
            fontSize: "0.95rem",
            textTransform: "none",
            background: added
              ? "linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)"
              : "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
            boxShadow: "0 4px 14px rgba(213,170,47,0.35)",
            "&:hover": {
              background: "linear-gradient(135deg, #b08c26 0%, #d5aa2f 100%)",
              boxShadow: "0 6px 20px rgba(213,170,47,0.45)",
            },
            "&:disabled": {
              background: added
                ? "linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)"
                : undefined,
              color: added ? "#fff" : undefined,
            },
          }}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : added ? (
            isAr ? "تمت الإضافة ✓" : "Added ✓"
          ) : (
            t("add_to_cart")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
